import { SimConstants } from "@highmarch/domain";

/**
 * How each pet's attack (the sim's PetBit: sim/petFighter.ts bite, sim/petStrike.ts strike) is staged, the parts that
 * need no GL (test/petStrike.test.ts pins them). The sim picks the raider nearest the flag's cell within the ability's
 * `reach` (1.5–3 cells) and applies the damage that tick; it has no pet position, and the view's pet sits beside the
 * formation (petMind.ts petSpot), up to 3.5 cells from the raider it hits.
 *
 * v1 (2026-10-23) ran the pet to the raider and cut to its standing `bite` clip. v2 (Kevin: "it's like they stop their
 * running / movement / flying animation whilst attacking"): the pet is steered (accelerate, cruise, brake, a little
 * overshoot), going round the troops and the other raiders and coming at its raider from the camera's side; its feet
 * keep the stride its speed asks for (run, walk, idle, cross-faded); the strike is layered over that stride
 * (petAnim.ts playStrike: the body lunges, the head snaps, the legs keep running); the owl flies (fly / hover / glide,
 * the `swoop` strike in the air). The sim's own forecast (domain petForecast) says who and when, so the blow is staged
 * to land on the sim's tick, and the sim's hit is held for it (statusFx.ts PET_ROUND) only when it could not be.
 */

/** How a pet's attack is staged: a run and a blow, a flight and a strike with the talons, or a spit from the mouth. */
export type PetStrikeStyle = "lunge" | "swoop" | "spit";

export interface PetStrikeSpec {
  readonly style: PetStrikeStyle;
  /**
   * Seconds into the strike clip at which the blow lands (the spit's release): the art's measured frames
   * (art/unitkit/anim_pet_v2.py and anim_bird.py BITE_IMPACT_FRAME f8, the birds' `swoop` f8 too, anim_kit.py f11,
   * anim_tortoise.py and anim_axolotl.py f10; frame f at (f − 1)/30 s).
   */
  readonly impact: number;
  /** How far its nose reaches ahead of its feet's centre at life size (anchors.json `half_z`, world units). */
  readonly nose: number;
  /**
   * How fast it runs at a raider, as its `run` clip's rate: the speed is the model's measured gait (anchors.json `gait`)
   * times this, so its paws stay planted (a tortoise scurries at a high rate rather than skating). The owl flies at
   * `fly` (world units a second at life size) instead.
   */
  readonly runRate: number;
  readonly fly?: number;
  /** The gallop it runs at a raider with: `run`, or the tortoise's `hurry` (a long-reaching trot authored for the fight). */
  readonly runClip: "run" | "hurry";
  /** A bird's strike in the air: the owl's talon `swoop`, the magpie's pecking `dive`. */
  readonly airClip?: "swoop" | "dive";
}

/** A fight's pace: cruise speed (world units a second), how hard it speeds up and brakes (units/s²), the trot / gallop switch. */
export interface Pace { readonly speed: number; readonly accel: number; readonly walkUnder: number }

/** Seconds to reach cruise from a stand (and to stop from it). */
const RAMP_SECONDS = 0.3;

/**
 * The pace for `spec` from its model's gaits at its drawn scale: `run` and `walk` are those gaits' natural speeds
 * (world units a second, petGait.ts naturalSpeed). The gallop holds above the speed the trot would need 1.4× its clip
 * rate for, or above the gallop's own slowest planted pace.
 */
export function paceOf(spec: PetStrikeSpec, run: number, walk: number, scale: number): Pace {
  const speed = spec.fly !== undefined ? spec.fly * scale : run * spec.runRate;
  return { speed, accel: speed / RAMP_SECONDS, walkUnder: Math.max(walk * 1.4, run * 0.55) };
}

const FRAME = 1 / 30;
/** The art's impact frame `f` as seconds into the clip (build_pet.py: frame f lands at (f − 1)/30 s). */
const at = (f: number): number => (f - 1) * FRAME;

export const PET_STRIKE: Readonly<Record<string, PetStrikeSpec>> = {
  muster_pup: { style: "lunge", impact: at(8), nose: 0.135, runRate: 1.6, runClip: "run" },
  lantern_owl: { style: "swoop", impact: at(8), nose: 0.104, runRate: 1.3, fly: 2.4, runClip: "run", airClip: "swoop" },
  ember_kit: { style: "lunge", impact: at(11), nose: 0.154, runRate: 1.6, runClip: "run" },
  crystal_tortoise: { style: "lunge", impact: at(10), nose: 0.154, runRate: 1.25, runClip: "hurry" },
  bubble_axolotl: { style: "spit", impact: at(10), nose: 0.237, runRate: 1.3, runClip: "run" },
  star_magpie: { style: "swoop", impact: at(8), nose: 0.168, runRate: 1.7, fly: 1.8, runClip: "run", airClip: "dive" },
};

/** A pet the table does not know (a future one) is staged as the pup. */
export function strikeOf(petId: string | null): PetStrikeSpec { return (petId && PET_STRIKE[petId]) || PET_STRIKE.muster_pup; }

export const PET_STRIKE_TUNING = {
  /** A raider's body round its centre (world units): the pet's nose stops this far from it. */
  bodyRadius: 0.13,
  /** The run reaches the raider this long before the blow: the last stride runs into the strike. */
  arriveLead: 0.06,
  /** A blow whose pet is still this far (world units) past its contact point at the blow waits for it, at most `maxWait` s. */
  contactSlack: 0.12, maxWait: 0.15,
  /** The spit's flight (world units a second), and its shortest and longest flight (seconds). */
  spitSpeed: 7, spitFlight: [0.12, 0.4] as const,
  /** The birds' flight: cruise height over the ground (world units at life size), how fast they climb or drop. */
  cruiseAlt: 0.32, climb: 2.2,
  /** A bird chasing a flyer aims this far ahead along the flyer's way (seconds of its pace, at most). */
  leadAhead: 0.6,
  /** It never strays past the attack's reach plus this (cells) from its spot: past it it lets go and comes back. */
  leash: 0.9,
  /** How high a blow lands on a ground raider, and how far under a flyer's body the owl hangs (world units). */
  hitHeight: 0.25, underFlyer: 0.2,
  /** After a blow it stays on the raider this many of its strike intervals (the next blow is due), then goes back. */
  stayIntervals: 1.3,
  /** It turns this fast (radians a second) while it fights. */
  turnRate: 10,
  /** A blow seen coming that the sim has not struck this many ticks after it was due is dropped (it guessed wrong). */
  lateTicks: 2,
  /** Where it comes at a raider from: this share toward the camera's side, the rest from where it comes. */
  cameraSide: 0.6,
  /** Steering round the troops and the other raiders: how close (world units) its way may pass one. */
  avoid: 0.28,
  /** Under this speed it is standing (world units a second); Pace.walkUnder parts the trot from the gallop. */
  still: 0.06,
  /** In the air: above this height it flies (fly / glide / hover), and above this speed it beats on rather than hovers. */
  airborne: 0.04, flyOver: 0.55,
} as const;

const TICK = 1 / SimConstants.TicksPerSecond;

/** How far from a raider's centre its feet stand for the blow (world units), drawn at `scale`: the raider's body and its nose. */
export function contactGap(spec: PetStrikeSpec, scale: number): number {
  return PET_STRIKE_TUNING.bodyRadius + spec.nose * scale;
}

/** Seconds a steered run of `dist` takes at `pace` from a stand to a stop (up to speed, on at it, braking), 0 for the spit. */
export function travelSeconds(spec: PetStrikeSpec, pace: Pace, dist: number): number {
  if (spec.style === "spit" || dist <= 1e-3) return 0;
  const v = pace.speed, a = pace.accel, ramp = (v * v) / a;            // the way covered speeding up and braking
  return dist >= ramp ? dist / v + v / a : 2 * Math.sqrt(dist / a);
}

/** Seconds the spit flies `dist` (its mouth to the raider). */
export function spitFlight(dist: number): number {
  const [lo, hi] = PET_STRIKE_TUNING.spitFlight;
  return Math.min(hi, Math.max(lo, dist / PET_STRIKE_TUNING.spitSpeed));
}

/**
 * One attack's timeline (pet clock, seconds): it sets off at `runAt`, the strike clip starts at `clipAt`, the blow
 * lands (the spit leaves the mouth) at `releaseAt`, the hit shows at `hitAt` (the blow, or the spit's landing).
 */
export interface StrikeTimes { readonly runAt: number; readonly clipAt: number; readonly releaseAt: number; readonly hitAt: number }

/** Seconds of lead an attack needs before its hit: the run to the raider, the clip's wind-up, the spit's flight. */
export function leadSeconds(spec: PetStrikeSpec, travel: number, flight = 0): number {
  return Math.max(travel + PET_STRIKE_TUNING.arriveLead, spec.impact) + flight;
}

/**
 * The timeline of an attack decided at `now` whose hit should show at `due` (the sim's tick, seen coming) or as soon as
 * it can (`due` null: the sim has struck already); `travel` the run's seconds, `flight` the spit's.
 */
export function strikeTimes(spec: PetStrikeSpec, now: number, due: number | null, travel: number, flight = 0): StrikeTimes {
  const soonest = now + leadSeconds(spec, travel, flight);
  const hitAt = due === null ? soonest : Math.max(due, soonest);
  const releaseAt = hitAt - flight;
  return { runAt: Math.max(now, releaseAt - PET_STRIKE_TUNING.arriveLead - travel), clipAt: releaseAt - spec.impact, releaseAt, hitAt };
}

/** The pet's clock when the sim's next blow lands, read after a tick: `inTicks` ticks on (domain petForecast). */
export function blowAt(now: number, inTicks: number): number { return now + inTicks * TICK; }

/** A point on the ground (world x / z), written in place. */
export interface Spot { x: number; z: number }

/**
 * Where its feet stand to strike a raider at (tx, tz) coming from (fx, fz): `gap` from the raider's centre, on the side
 * between the one it comes from and the camera's (`cx`, `cz`: the unit ground direction toward the camera), so the
 * blow is seen. Written into `out`.
 */
export function contactSide(fx: number, fz: number, tx: number, tz: number, cx: number, cz: number, gap: number, out: Spot): Spot {
  let dx = fx - tx, dz = fz - tz;
  const d = Math.hypot(dx, dz);
  if (d > 1e-6) { dx /= d; dz /= d; } else { dx = cx; dz = cz; }
  const w = PET_STRIKE_TUNING.cameraSide;
  let ux = dx * (1 - w) + cx * w, uz = dz * (1 - w) + cz * w;
  const u = Math.hypot(ux, uz);
  if (u < 1e-6) { ux = dx; uz = dz; } else { ux /= u; uz /= u; }
  out.x = tx + ux * gap; out.z = tz + uz * gap;
  return out;
}

/** The last stretch onto the goal closes at this time constant (s): a smooth settle, no hunting round the spot. */
const SETTLE_SECONDS = 0.08;

/** A steered body on the ground: where it is and its velocity (world units, units a second), written in place. */
export interface Mover { x: number; z: number; vx: number; vz: number }

/**
 * One step (`dt` s) of an "arrive" steer toward (gx, gz): it speeds up at `accel` to `speed`, brakes to stop on the goal,
 * and steers past the side of the nearest of `obs` (x, z pairs; `n` of them) standing within PET_STRIKE_TUNING.avoid
 * of its straight way. No allocation.
 */
export function steer(m: Mover, gx: number, gz: number, speed: number, accel: number, dt: number, obs: ArrayLike<number> = [], n = 0): void {
  const T = PET_STRIKE_TUNING;
  let dx = gx - m.x, dz = gz - m.z;
  const d = Math.hypot(dx, dz);
  // brake a little early (it lands, never slams), and settle the last centimetres smoothly (no jitter on the spot)
  const want = Math.min(speed, Math.sqrt(2 * accel * 0.85 * d), d / SETTLE_SECONDS);
  if (d > 1e-6) {
    // the nearest thing standing across the straight way: aim past its side instead (the side it leans away from)
    const ux = dx / d, uz = dz / d;
    let best = Infinity, px = 0, pz = 0;
    for (let i = 0; i < n; i++) {
      const ox = obs[2 * i] - m.x, oz = obs[2 * i + 1] - m.z;
      const along = ox * ux + oz * uz, across = ox * uz - oz * ux;
      if (along <= 0 || along >= d - T.avoid * 0.5 || Math.abs(across) >= T.avoid || along >= best) continue;
      const side = across >= 0 ? -1 : 1, r = T.avoid * 1.25;
      best = along; px = obs[2 * i] + uz * side * r; pz = obs[2 * i + 1] - ux * side * r;
    }
    if (best < Infinity) { dx = px - m.x; dz = pz - m.z; }
  }
  const e = Math.hypot(dx, dz);
  const wx = e > 1e-6 ? (dx / e) * want : 0, wz = e > 1e-6 ? (dz / e) * want : 0;
  const ax = wx - m.vx, az = wz - m.vz, a = Math.hypot(ax, az), lim = accel * dt;
  const k = a > lim ? lim / a : 1;
  m.vx += ax * k; m.vz += az * k;
  m.x += m.vx * dt; m.z += m.vz * dt;
}

/** The locomotion loop for a speed (world units a second) on the ground, or a height and speed in the air. */
export function gaitOf(speed: number, lift: number, climbing: number, walkUnder: number): "idle" | "walk" | "run" | "fly" | "hover" | "glide" {
  const T = PET_STRIKE_TUNING;
  if (lift > T.airborne) return speed > T.flyOver ? (climbing < -0.2 ? "glide" : "fly") : "hover";
  return speed < T.still ? "idle" : speed < walkUnder ? "walk" : "run";
}
